import { useMemo } from 'react';
import {
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Area,
    ComposedChart,
    Line,
} from 'recharts';

interface Props {
    weightedVol: number; // e.g., 0.15 for 15%
    avgConviction: number; // e.g., 0.5 z-score
    investmentAmount?: number;
}

export const PortfolioProjectionChart = ({
    weightedVol,
    avgConviction,
    investmentAmount = 10000,
}: Props) => {
    const projectionData = useMemo(() => {
        const data = [];
        const months = 12;

        // Drift + Alpha + Risk-Premium model (mirrors the backend ER engine).
        // The expected return scales with BOTH the book's conviction AND the
        // volatility it takes on, so higher-risk profiles project higher growth.
        // This is what makes the chart respond to the Investor Risk Tolerance
        // slider: weightedVol and avgConviction both change per risk profile.
        const marketDrift = 0.08; // elite-universe baseline annual drift
        const convictionAlpha = Math.max(avgConviction, 0) * 0.05; // +1σ ≈ +5%
        const riskPremium = weightedVol * 0.3; // reward for volatility taken
        const expectedAnnualReturn = marketDrift + convictionAlpha + riskPremium;
        const monthlyReturn = expectedAnnualReturn / 12;
        const monthlyVol = weightedVol / Math.sqrt(12);

        let currentExpected = investmentAmount;

        for (let month = 0; month <= months; month++) {
            if (month === 0) {
                data.push({
                    month: 'Today',
                    expected: currentExpected,
                    bull: currentExpected,
                    bear: currentExpected,
                });
                continue;
            }

            currentExpected = currentExpected * (1 + monthlyReturn);

            const timeScaling = Math.sqrt(month);
            const volImpact = currentExpected * (monthlyVol * timeScaling * 1.96);

            data.push({
                month: `Month ${month}`,
                expected: Math.round(currentExpected),
                bull: Math.round(currentExpected + volImpact),
                bear: Math.round(currentExpected - volImpact),
            });
        }
        return data;
    }, [weightedVol, avgConviction, investmentAmount]);

    const finalExpected = projectionData[12].expected;
    const projectedReturnPct =
        ((finalExpected - investmentAmount) / investmentAmount) * 100;

    return (
        <div className="surface-card flex flex-col flex-1 animate-fade-in min-h-[350px]">
            <div className="flex justify-between items-start mb-6">
                <div>
                    <h3 className="text-sm font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">
                        12-Month Projection
                    </h3>
                    <p className="text-xs text-slate-500 mt-1">
                        Expected Growth of $10k (95% Confidence)
                    </p>
                </div>
                <div className="text-right">
                    <p className="text-2xl font-black text-emerald-600 dark:text-emerald-400">
                        ${finalExpected.toLocaleString()}
                    </p>
                    <p className="text-xs font-bold text-slate-500 uppercase">
                        +{projectedReturnPct.toFixed(1)}% Expected
                    </p>
                </div>
            </div>

            <div className="flex-1 w-full">
                <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                        data={projectionData}
                        margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                    >
                        <defs>
                            <linearGradient
                                id="colorExpected"
                                x1="0"
                                y1="0"
                                x2="0"
                                y2="1"
                            >
                                <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                                <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid
                            strokeDasharray="3 3"
                            vertical={false}
                            stroke="#334155"
                            opacity={0.2}
                        />
                        <XAxis
                            dataKey="month"
                            tick={{ fontSize: 10, fill: '#64748b' }}
                            axisLine={false}
                            tickLine={false}
                        />
                        <YAxis
                            domain={['auto', 'auto']}
                            tickFormatter={(val) => `$${(val / 1000).toFixed(0)}k`}
                            tick={{ fontSize: 10, fill: '#64748b' }}
                            axisLine={false}
                            tickLine={false}
                        />
                        <Tooltip
                            formatter={(value: number) => `$${value.toLocaleString()}`}
                            contentStyle={{
                                backgroundColor: '#1e293b',
                                border: 'none',
                                borderRadius: '8px',
                                color: '#f8fafc',
                            }}
                            itemStyle={{ fontSize: '13px', fontWeight: 'bold' }}
                            labelStyle={{ color: '#94a3b8', marginBottom: '4px' }}
                        />

                        <Area type="monotone" dataKey="bull" fill="none" stroke="none" />
                        <Area
                            type="monotone"
                            dataKey="bear"
                            fill="#e2e8f0"
                            fillOpacity={0.1}
                            stroke="none"
                            className="dark:fill-slate-700/50"
                        />

                        <Line
                            type="monotone"
                            dataKey="expected"
                            stroke="#10b981"
                            strokeWidth={3}
                            dot={false}
                            activeDot={{
                                r: 6,
                                fill: '#10b981',
                                stroke: '#fff',
                                strokeWidth: 2,
                            }}
                        />
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};
